import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { map, mergeMap, tap } from 'rxjs/operators';
import { IdName } from './oem-dealer.service';
import { BehaviorSubject, of } from 'rxjs';
import { OpenService } from './open.service';
import { EvaluationVehicle } from '@app-model/evaluation';
import { ToastrService } from 'ngx-toastr';
import { DecimalPipe, PercentPipe } from '@angular/common';
import { ReportStatus, Vehicle } from '@app-model/vehicle';
import {
  buildFipeComparisonFromDiff,
  FipeComparisonModeInput,
} from '@app-utils/fipe-comparison.utils';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private cacheEquipaments = [];

  decimalPipe = new DecimalPipe('pt-BR');
  percentPipe = new PercentPipe('pt-BR');

  public infoShare: string = `1º - Copie a imagem e cole no seu aplicativo de mensagem.
  2º - Copie a descrição e cole na descrição da imagem.
  3º - Envie a mensagem e compartilhe com seus contatos.`;

  constructor(private http: HttpClient,
    private openService: OpenService,
    private toast: ToastrService) { }

  getEquipmentOptions() {
    if (this.cacheEquipaments.length > 0) {
      return of(this.cacheEquipaments);
    }
    return this.http.get(`${environment.api}/options/equipmentOptions`).pipe(
      map((response: IdName[]) => response),
      tap(response => {
        this.cacheEquipaments = response;
      })
    );
  }

  private getIconBySegment(segment: string): string {
    if(!segment) {
      return '🚗';
    }

    switch (segment) {
      case 'CAR':
        return '🚗';
      case 'TRUCK':
        return '🚚';
      case 'MOTORCYCLE':
        return '🏍️';
      default:
        return '🚗';
    }
  }


  async buildFastQuoteUrl(vehicle: EvaluationVehicle | Vehicle): Promise<string> {
    const url = await this.openService.getUrl() || environment.url;

    return `${url}/compartilhar/cotacao-rapida/veiculos/${this.openService.getVehicleSlug(vehicle)}/${vehicle.fastQuoteId
      }`;
  }

  public vehicleDescription(
    vehicle: Vehicle | EvaluationVehicle,
    displayMode?: FipeComparisonModeInput,
  ): string {
    let message = `
            ${vehicle.oemDealer}

${this.getIconBySegment(vehicle.segment)} ${vehicle.makeName} ${vehicle.modelName} ${vehicle.trimWithNoModel} ${vehicle.manufactureYear}/${vehicle.modelYear}

🛣️ Quilometragem: ${this.decimalPipe.transform(vehicle.km)}
🏙️ Cidade: ${vehicle.city} / ${vehicle.uf}`;

    if(vehicle.fipePrice) {
      message = message.concat(`
💰 FIPE: R$${this.decimalPipe.transform(vehicle.fipePrice)}
`);
    }

    if (vehicle instanceof Vehicle) {
      message = message.concat(`
📋 Laudo: ${vehicle.reportStatus ? ReportStatus[vehicle.reportStatus] : 'N/A'}`)

      if(vehicle.initialBid) {
        const fipeComparison = buildFipeComparisonFromDiff(vehicle.fipeDiff, displayMode);
        const fipeComparisonText = fipeComparison
          ? `${fipeComparison.text}${fipeComparison.suffix ? ` ${fipeComparison.suffix}` : ''}`
          : this.percentPipe.transform(vehicle.fipeDiff);

        message = message.concat(`
💥 Oferta Inicial: R$${this.decimalPipe.transform(vehicle.initialBid)}
        ${fipeComparisonText}`)
      }
    }

    return message;
  }


  copyDescription(
    vehicle: Vehicle | EvaluationVehicle,
    slugUrl: string,
    displayMode?: FipeComparisonModeInput,
  ) {
    let message = this.vehicleDescription(vehicle, displayMode);

    message = message.concat(`

FAÇA SUA OFERTA AGORA!
${slugUrl}?utm_source=comercial&utm_medium=whatsapp`)

    navigator.clipboard.writeText(message).then(() => this.toast.success("Copiado com sucesso!"))
      .catch(err => {
        this.toast.error("Não foi possível copiar a descrição");
        console.error(err);
      })
  }

  convertToPng(imgBlob): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(imgBlob);

      img.onload = () => {
        // Criar um canvas e desenhar a imagem nele
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        // Obter a imagem como PNG
        canvas.toBlob((pngBlob) => {
          resolve(pngBlob);
        }, 'image/png');
      };

      img.onerror = (e) => {
        reject(e);
      };
    });
  }

  createImage(options) {
    options = options || {};
    const img = (Image) ? new Image() : document.createElement("img");
    if (options.src) {
      img.src = options.src;
    }
    return img;
  }

  async copyImage(imgUrl: string) {
    try {

      const makeImagePromise = async () => {
        const response = await fetch(imgUrl, { mode: 'cors', method: 'GET', cache: 'no-cache', });
        const imgBlob = await response.blob();

        return await this.convertToPng(imgBlob);
      }

      (navigator.clipboard as any).write([new globalThis.ClipboardItem({ "image/png": makeImagePromise() })])
        .then(() => this.toast.success('Copiado com sucesso!'))
        .catch((error) => {
          console.log(error);
          this.toast.error("Não foi possível copiar a imagem.")
        });
    } catch (err) {
      this.toast.error("Não foi possível copiar.");
      console.error(err);
    }
  }
}
